import axios from "axios";
import type {
    AttendanceRequestResult,
    IAttendanceCommandHistoryQuery,
    IAttendanceManagementCommandHistoryResponse,
    IAttendanceManagementCommandHistoryResponseDto,
    IAttendanceManagementConfiguration,
    IAttendanceManagementConfigurationDto,
    IAttendanceManagementWritebackSummaryResponseDto,
    IAttendanceManagementWritebackSummaryRow,
    IAttendanceOperationalRangeQuery,
    IAttendanceRequestError,
    IAttendanceRequestFieldError,
} from "../models/AttendanceManagement";
import BaseService from "./BaseService";
import {
    toAttendanceManagementSaveRequest,
    toAttendanceManagementViewModel,
    toCommandHistoryResponse,
    toWritebackSummaryRows,
    validateAttendanceManagementConfiguration,
    validateCommandHistoryQuery,
    validateOperationalRange,
} from "./AttendanceManagementMapper";

type QueryParameters = Record<string, string | number>;

const knownManagementFields = [
    "DefaultTimingConfiguration",
    "TimingConfigurations",
    "ProtectedMarks",
    "OverwritableMarks",
    "EnabledCodes",
    "FromUtc",
    "ToUtc",
    "Page",
    "PageSize",
    "Class",
    "Status",
    "Search",
];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function toSafeString(value: unknown, fallback: string): string {
    if (typeof value !== "string") {
        return fallback;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed.slice(0, 500) : fallback;
}

function isKnownManagementField(field: string): boolean {
    return knownManagementFields.some((knownField) => (
        field === knownField
        || field.startsWith(`${knownField}.`)
        || field.startsWith(`${knownField}[`)
    ));
}

function getStructuredValidationErrors(data: unknown): {
    fieldErrors: IAttendanceRequestFieldError[];
    formErrors: IAttendanceRequestFieldError[];
} {
    if (!isRecord(data) || !Array.isArray(data.errors)) {
        return { fieldErrors: [], formErrors: [] };
    }

    return data.errors.reduce<{
        fieldErrors: IAttendanceRequestFieldError[];
        formErrors: IAttendanceRequestFieldError[];
    }>((errors, value) => {
        if (!isRecord(value)) {
            return errors;
        }

        const field = toSafeString(value.field, "Form");
        const error: IAttendanceRequestFieldError = {
            field,
            code: toSafeString(value.code, "VALIDATION_ERROR"),
            message: toSafeString(value.message, "Attendance management validation failed."),
        };

        if (isKnownManagementField(field)) {
            errors.fieldErrors.push(error);
        } else {
            errors.formErrors.push(error);
        }

        return errors;
    }, { fieldErrors: [], formErrors: [] });
}

function isAxiosShapedError(value: unknown): value is {
    isAxiosError?: boolean;
    response?: {
        status?: unknown;
        data?: unknown;
    };
} {
    return axios.isAxiosError(value)
        || (isRecord(value) && value.isAxiosError === true);
}

function toRequestError(status?: number, data?: unknown): IAttendanceRequestError {
    if (status === 400) {
        const validationErrors = getStructuredValidationErrors(data);

        return {
            category: "validation",
            message: "Some attendance management values need attention.",
            retryable: false,
            status,
            fieldErrors: validationErrors.fieldErrors.length > 0 ? validationErrors.fieldErrors : undefined,
            formErrors: validationErrors.formErrors.length > 0 ? validationErrors.formErrors : undefined,
        };
    }

    if (status === 401) {
        return {
            category: "authentication",
            message: "Authentication is required to access attendance management.",
            retryable: false,
            status,
        };
    }

    if (status === 403) {
        return {
            category: "authorization",
            message: "You are not authorized to access attendance management.",
            retryable: false,
            status,
        };
    }

    if (status === 404) {
        return {
            category: "notFound",
            message: "The requested attendance management resource was not found.",
            retryable: false,
            status,
        };
    }

    if (status === 409) {
        return {
            category: "conflict",
            message: "Attendance management data changed. Refresh and try again.",
            retryable: false,
            status,
        };
    }

    if (status === 422) {
        return {
            category: "unprocessable",
            message: "Attendance writeback is not currently available.",
            retryable: false,
            status,
        };
    }

    if (status === 429) {
        return {
            category: "rateLimited",
            message: "Attendance management is temporarily busy. Try again.",
            retryable: true,
            status,
        };
    }

    if (status !== undefined && status >= 500) {
        return {
            category: "transient",
            message: "Attendance management is temporarily unavailable. Try again.",
            retryable: true,
            status,
        };
    }

    return {
        category: "transport",
        message: "Unable to reach attendance management. Try again.",
        retryable: true,
        status,
    };
}

function toLocalValidationFailure(
    fieldErrors: IAttendanceRequestFieldError[],
): IAttendanceRequestFailure {
    return {
        ok: false,
        error: {
            category: "validation",
            message: "Some attendance management values need attention.",
            retryable: false,
            fieldErrors,
        },
    };
}

interface IAttendanceRequestFailure {
    ok: false;
    error: IAttendanceRequestError;
}

function isResponseWithData(value: unknown): value is { data: unknown } {
    return isRecord(value)
        && Object.prototype.hasOwnProperty.call(value, "data")
        && value.data !== undefined;
}

function toQueryParameters(query: IAttendanceCommandHistoryQuery): QueryParameters {
    const parameters: QueryParameters = {
        fromUtc: query.fromUtc,
        toUtc: query.toUtc,
        page: query.page,
        pageSize: query.pageSize,
    };

    if (query.class !== undefined) {
        parameters.class = query.class;
    }

    if (query.status !== undefined) {
        parameters.status = query.status;
    }

    if (query.search !== undefined) {
        parameters.search = query.search;
    }

    return parameters;
}

export function toAttendanceRequestError(error: unknown): IAttendanceRequestError {
    if (!isAxiosShapedError(error)) {
        return toRequestError();
    }

    const status = typeof error.response?.status === "number" ? error.response.status : undefined;
    return toRequestError(status, error.response?.data);
}

export default class AttendanceManagementService extends BaseService {
    controllerName = "establishment";

    private async getMapped<TDto, TValue>(
        action: string,
        query: QueryParameters,
        mapper: (response: TDto) => TValue,
    ): Promise<AttendanceRequestResult<TValue>> {
        try {
            const response = await this.Get(action, query);

            if (isAxiosShapedError(response)) {
                return { ok: false, error: toAttendanceRequestError(response) };
            }

            if (!isResponseWithData(response)) {
                return { ok: false, error: toRequestError() };
            }

            return { ok: true, value: mapper(response.data as TDto) };
        } catch (error: unknown) {
            return { ok: false, error: toAttendanceRequestError(error) };
        }
    }

    async GetConfiguration(
        establishmentId: string,
    ): Promise<AttendanceRequestResult<IAttendanceManagementConfiguration>> {
        return this.getMapped<IAttendanceManagementConfigurationDto, IAttendanceManagementConfiguration>(
            `${establishmentId}/attendance/management/configuration`,
            {},
            toAttendanceManagementViewModel,
        );
    }

    async SaveConfiguration(
        establishmentId: string,
        configuration: IAttendanceManagementConfiguration,
    ): Promise<AttendanceRequestResult<IAttendanceManagementConfiguration>> {
        const validationErrors = validateAttendanceManagementConfiguration(configuration);

        if (validationErrors.length > 0) {
            return toLocalValidationFailure(validationErrors);
        }

        try {
            const response = await this.Put(
                `${establishmentId}/attendance/management/configuration`,
                toAttendanceManagementSaveRequest(configuration),
            );

            if (isAxiosShapedError(response)) {
                return { ok: false, error: toAttendanceRequestError(response) };
            }

            if (!isResponseWithData(response)) {
                return { ok: false, error: toRequestError() };
            }

            return {
                ok: true,
                value: toAttendanceManagementViewModel(
                    response.data as IAttendanceManagementConfigurationDto,
                ),
            };
        } catch (error: unknown) {
            return { ok: false, error: toAttendanceRequestError(error) };
        }
    }

    async GetWritebackSummary(
        establishmentId: string,
        query: IAttendanceOperationalRangeQuery,
    ): Promise<AttendanceRequestResult<IAttendanceManagementWritebackSummaryRow[]>> {
        const validationErrors = validateOperationalRange(query);

        if (validationErrors.length > 0) {
            return toLocalValidationFailure(validationErrors);
        }

        return this.getMapped<
            IAttendanceManagementWritebackSummaryResponseDto,
            IAttendanceManagementWritebackSummaryRow[]
        >(
            `${establishmentId}/attendance/management/writeback-summary`,
            { fromUtc: query.fromUtc, toUtc: query.toUtc },
            toWritebackSummaryRows,
        );
    }

    async GetCommandHistory(
        establishmentId: string,
        query: IAttendanceCommandHistoryQuery,
    ): Promise<AttendanceRequestResult<IAttendanceManagementCommandHistoryResponse>> {
        const validationErrors = validateCommandHistoryQuery(query);

        if (validationErrors.length > 0) {
            return toLocalValidationFailure(validationErrors);
        }

        return this.getMapped<
            IAttendanceManagementCommandHistoryResponseDto,
            IAttendanceManagementCommandHistoryResponse
        >(
            `${establishmentId}/attendance/management/command-history`,
            toQueryParameters(query),
            toCommandHistoryResponse,
        );
    }
}